import {useMemo} from 'react';
import DataClientContext from 'src/internal/revamp/config/DataClientContext';
import DataConfigContext from 'src/internal/revamp/config/DataConfigContext';
import createGraphQlDataClient from 'src/internal/revamp/config/createGraphQlDataClient';
import RawFetchCache from 'src/internal/revamp/fetch/RawFetchCache';
import useObservabilityFns from 'src/internal/shared/observability/useObservabilityFns';

import type {ReactElement, ReactNode} from 'react';
import type {
  DataClient,
  DataClientConfig,
  DataConfig,
  GraphQlDataClient,
} from 'src/internal/revamp/config/types';
import type {Fetch} from 'src/internal/shared/fetch/types';

type DataClientProviderProps = {
  children: ReactNode;
  config: DataClientConfig;
};

function DataClientProvider({
  children,
  config,
}: DataClientProviderProps): ReactElement {
  const rawFetchCache = useMemo(() => new RawFetchCache(), []);
  const observabilityFns = useObservabilityFns();
  const {graphql: graphQlConfig, fetch: fetchFunction} = config;

  const graphQlDataClient: GraphQlDataClient | undefined = useMemo(
    () =>
      createGraphQlDataClient(graphQlConfig, fetchFunction, observabilityFns),
    [graphQlConfig, fetchFunction, observabilityFns],
  );

  const dataClient: DataClient = useMemo(
    () => ({
      graphQlDataClient,
      fetchFunction,
      rawFetchCache,
    }),
    [graphQlDataClient, fetchFunction, rawFetchCache],
  );

  return (
    <DataClientContext.Provider value={dataClient}>
      {children}
    </DataClientContext.Provider>
  );
}

type OuterProps = {
  children: ReactNode;
  config?: DataConfig;
  fetchFromOldProvider?: Fetch;
};

export default function DataRevampLayer({
  children,
  config,
  fetchFromOldProvider,
}: OuterProps): ReactElement {
  const normalizedFetch = fetchFromOldProvider;
  // This ensures that the revamped provider is also getting the fetch version with
  // normalized headers
  const configWithFetch: DataClientConfig | undefined = useMemo(() => {
    if (typeof config === 'undefined') {
      return undefined;
    }

    return {
      ...config,
      fetch: normalizedFetch || globalThis.fetch,
    };
  }, [config, normalizedFetch]);

  if (typeof configWithFetch === 'undefined') {
    return <>{children}</>;
  }

  return (
    <DataConfigContext.Provider value={configWithFetch}>
      <DataClientProvider config={configWithFetch}>
        {children}
      </DataClientProvider>
    </DataConfigContext.Provider>
  );
}
